import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Switch, Route, useParams, useLocation, useHistory } from 'react-router-dom'
import { Spin, message } from 'antd'
import axios from 'axios'
import { OrganisationLayout } from './OrganisationLayout'
import { OrganisationParams } from './OrganisationParams'
import { OpenCollaborators } from '../collaborators/OpenCollaborators'
import { CollaboratorForm } from '../collaborators/CollaboratorForm'
import { OpenSoftwares } from '../softwares/OpenSoftwares'
import { OpenHierarchy } from './OpenHierarchy'
import { OpenAdhocRapports } from './OpenAdhocRapports'
import { CnSoftAccessPage } from '../cn-softaccess/CnSoftAccessPage'
import { ConquestSoftAccessPage } from '../softwares/ConquestSoftAccessPage'
import { ConquestSoftAccessShowPdvPage } from '../softwares/ConquestSoftAccessShowPdvPage'
import { AdhocRoutes } from '../adhoc/AdhocRoutes'
import { ITrame } from '../adhoc/util'

interface OrganisationAppProps {
  orgaId: string
  orgaName: string
  adhocData: ITrame[]
}

// CollaboratorEditRoute : route pour la page d'édition d'un collaborateur
function CollaboratorEditRoute({ orgaId }: { orgaId: string }) {
  const { collId } = useParams<{ collId: string }>()
  return <CollaboratorForm orgaId={orgaId} collId={collId} />
}

/** Redirect legacy Données S&C URLs into the unified softaccess page (Données tab). */
function CnOrgaDataRedirect({ orgaId }: { orgaId: string }) {
  const history = useHistory()
  const location = useLocation()
  const [redirecting, setRedirecting] = useState(true)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const oldTab = new URLSearchParams(location.search).get('tab')
      const sub =
        oldTab === 'caFile' || oldTab === 'collsAgenciesOrRg2' || oldTab === 'ape' ? oldTab : undefined
      const params = new URLSearchParams({ tab: 'donnees' })
      if (sub && sub !== 'collsAgenciesOrRg2') params.set('sub', sub)

      try {
        const { data } = await axios.get<{ cn: Array<{ softaccess_id: string }> }>(
          `/organisations/${orgaId}/softwares/list`,
        )
        if (cancelled) return
        const cnId = data.cn[0]?.softaccess_id
        if (cnId) {
          history.replace(`/organisations/${orgaId}/cn/softwaresaccesses/${cnId}?${params.toString()}`)
        } else {
          history.replace(`/organisations/${orgaId}/cn/softwaresaccesses/new?${params.toString()}`)
        }
      } catch (error) {
        console.error(error)
        if (!cancelled) {
          message.error('Impossible de rediriger vers les données S&C')
          history.replace(`/organisations/${orgaId}/softwares`)
        }
      } finally {
        if (!cancelled) setRedirecting(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [history, location.search, orgaId])

  if (!redirecting) return null
  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <Spin size="large" />
    </div>
  )
}

// CnSoftAccessRoute : route pour la création / édition d'un accès Scan & Conquest
function CnSoftAccessRoute({ orgaId }: { orgaId: string }) {
  const { softaccessId } = useParams<{ softaccessId: string }>()
  return <CnSoftAccessPage orgaId={orgaId} softaccessId={softaccessId} />
}

function ConquestSoftAccessRoute({ orgaId }: { orgaId: string }) {
  const { softaccessId } = useParams<{ softaccessId: string }>()
  return <ConquestSoftAccessPage orgaId={orgaId} softaccessId={softaccessId} />
}

function ConquestSoftAccessShowPdvRoute({ orgaId }: { orgaId: string }) {
  const { softaccessId } = useParams<{ softaccessId: string }>()
  return <ConquestSoftAccessShowPdvPage orgaId={orgaId} softaccessId={softaccessId} />
}

function OrganisationRoutes({ orgaId, orgaName, adhocData }: OrganisationAppProps) {
  const base = `/organisations/${orgaId}`

  return (
    <OrganisationLayout orgaId={orgaId} orgaName={orgaName}>
      <Switch>
        <Route exact path={`${base}/collaborators/hierarchy`}>
          <OpenHierarchy orgaId={orgaId} />
        </Route>
        <Route exact path={`${base}/collaborators/new`}>
          <CollaboratorForm orgaId={orgaId} />
        </Route>
        <Route exact path={`${base}/collaborators/:collId/edit`}>
          <CollaboratorEditRoute orgaId={orgaId} />
        </Route>
        <Route exact path={`${base}/collaborators`}>
          <OpenCollaborators orgaId={orgaId} />
        </Route>
        <Route exact path={`${base}/softwares`}>
          <OpenSoftwares orgaId={orgaId} />
        </Route>
        <Route exact path={`${base}/conquest/softwaresaccesses/new`}>
          <ConquestSoftAccessPage orgaId={orgaId} softaccessId="new" />
        </Route>
        <Route exact path={`${base}/conquest/softwaresaccesses/:softaccessId/access`}>
          <ConquestSoftAccessShowPdvRoute orgaId={orgaId} />
        </Route>
        <Route exact path={`${base}/conquest/softwaresaccesses/:softaccessId`}>
          <ConquestSoftAccessRoute orgaId={orgaId} />
        </Route>
        <Route exact path={`${base}/cn/softwaresaccesses/new`}>
          <CnSoftAccessPage orgaId={orgaId} softaccessId="new" />
        </Route>
        <Route exact path={`${base}/cn/softwaresaccesses/:softaccessId`}>
          <CnSoftAccessRoute orgaId={orgaId} />
        </Route>
        <Route exact path={`${base}/datacnegoce/cnOrgaData`}>
          <CnOrgaDataRedirect orgaId={orgaId} />
        </Route>
        <Route exact path={`${base}/etudesadhoc/rapports`}>
          <OpenAdhocRapports orgaId={orgaId} />
        </Route>
        <Route path={`${base}/etudesadhoc`}>
          <AdhocRoutes oid={orgaId} trames={adhocData} />
        </Route>
        <Route exact path={[`${base}/params`, base]}>
          <OrganisationParams orgaId={orgaId} />
        </Route>
      </Switch>
    </OrganisationLayout>
  )
}

export function OrganisationApp({ orgaId, orgaName, adhocData }: OrganisationAppProps) {
  return (
    <Router>
      <OrganisationRoutes orgaId={orgaId} orgaName={orgaName} adhocData={adhocData} />
    </Router>
  )
}
